import InfoIcon from "@mui/icons-material/Info";
import { Alert, Box, Tooltip } from "@mui/material";
import CopyIconButton from "components/IconButtons/CopyIconButton";
import QrCodeButton from "components/IconButtons/QrCodeButton";
import { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import SortableTable from "components/SortableTable";
import useGlobalContext from "hooks/globalData";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { DirectLink } from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { SnackbarMessageProps } from "types/common-types";
import SkuLinkQrCodeDialog from "./SkuLinkQrCodeDialog";
import { withErrorBoundary } from "hocs/withErrorBoundary";

type SkuLinksTableProps = {
  skuId: string;
};

const SkuLinksTable = ({ skuId }: SkuLinksTableProps) => {
  const { formatMessage: t } = useIntl();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const { skuService } = useGlobalContext();
  const { organizationId } = contextStore.useState();
  const [openedSkuLink, setOpenedSkuLink] = useState<DirectLink>();

  const { data, isLoading } = useStrictQuery(
    ["directLinks", skuId, organizationId],
    () => skuService.getDirectLinks(skuId, organizationId!),
    { enabled: !!skuId && !!organizationId }
  );

  const handleCopyButtonClick = (directLink: DirectLink) => {
    navigator.clipboard.writeText(directLink.url);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "copySuccessMessage" }))
    );
  };

  const Actions = ({ value }: { value: DirectLink }) => {
    return (
      <>
        <CopyIconButton
          size="small"
          onClick={() => handleCopyButtonClick(value)}
          data-cy="copyButton"
        />
        <QrCodeButton
          size="small"
          onClick={() => setOpenedSkuLink(value)}
          data-cy="qrCodeButton"
        />
      </>
    );
  };

  const SkuLinkName = ({ value }: { value: DirectLink }) => {
    return (
      <Box display="flex">
        <Box mr={1}>{value.name}</Box>
        <Tooltip title={value.info ?? ""}>
          <InfoIcon fontSize="small" />
        </Tooltip>
      </Box>
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "node",
      View: SkuLinkName,
      label: t({ id: "targetPage" }),
    },
    {
      accessor: "url",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "urlLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 0,
      type: "node",
      View: Actions,
      noSort: true,
      label: t({ id: "actions" }),
    },
  ];

  const defaultOrderBy = ""; //Do not sort the table by default
  const skuLinks = data?.data ?? [];

  return (
    <>
      {organizationId ? (
        <SortableTable
          {...{
            rows: skuLinks.map((sl) => ({ ...sl, id: sl.name })),
            loading: isLoading,
            dense: true,
            cells,
            tableRowPerPage: 10,
            defaultOrderBy,
          }}
        />
      ) : (
        <Box width="100%">
          <Alert severity="warning">
            <T id="skuLinksAreAppInstanceSpecific" />
            <br />
            <T id="setAppInstanceToSeeSkuLinks" />
          </Alert>
        </Box>
      )}
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
      <SkuLinkQrCodeDialog
        open={!!openedSkuLink}
        skuLink={openedSkuLink}
        onClose={() => setOpenedSkuLink(undefined)}
      />
    </>
  );
};

export default withErrorBoundary(SkuLinksTable);
